/* eslint-disable @next/next/no-img-element */
import {
  LeagueAdvancedStatsPlayerFragment,
  LeagueAdvancedStatsTeamFragment,
} from '@amf/shared/types/graphql';
import useLeagueAdvancedStats from '@amf/shared/data/hooks/useLeagueAdvancedStats';
import { useTranslate } from '@tolgee/react';
import Link from 'next/link';
import React, { MouseEventHandler, useCallback } from 'react';
import { getInitials } from '@amf/shared/utils/string';
import Loader from '@amf/shared/components/utils/Loader';
import {
  LeagueAdvancedStatsPlayerMetric,
  LeagueAdvancedStatsTeamMetric,
} from '@amf/shared/data/League';
import { useRouter } from 'next/router';
import Button from '@amf/shared/components/button/Button';

interface PlayerProps {
  title: string;
  value: string;
  stats: LeagueAdvancedStatsPlayerFragment;
  prefixes: { stats: string; player: string };
  leagueId: string;
  metric: LeagueAdvancedStatsPlayerMetric | LeagueAdvancedStatsTeamMetric;
}

function PlayerCard({ title, stats, value, prefixes, leagueId, metric }: PlayerProps) {
  const { t } = useTranslate();
  const { push } = useRouter();
  const presentDetailStats = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async e => {
      e.stopPropagation();
      e.preventDefault();
      await push(`${prefixes.stats}/${leagueId}/${metric}`);
    },
    [leagueId, metric, prefixes.stats, push],
  );

  return (
    <Link href={`${prefixes.player}/${stats.player.id}`}>
      <a className='LeagueAdvancedStats__card'>
        <div className='LeagueAdvancedStats__cardPhoto'>
          {stats.player.photoUrl && <img src={stats.player.photoUrl} alt={stats.player.fullName} />}
          {!stats.player.photoUrl && (
            <div>{getInitials(stats.player.givenName, stats.player.familyName)}</div>
          )}
          {stats.player.currentTeam && (
            <div className='LeagueAdvancedStats__cardTeam'>
              {stats.player.currentTeam.club.brandUrl ? (
                <img
                  src={stats.player.currentTeam.club.brandUrl}
                  alt={stats.player.currentTeam.name}
                />
              ) : (
                <div>{stats.player.currentTeam.name.substring(0, 2)}</div>
              )}
            </div>
          )}
        </div>
        <div className='LeagueAdvancedStats__cardContent'>
          <div className='LeagueAdvancedStats__cardName'>{stats.player.fullName}</div>
          {stats.player.currentTeam && (
            <div className='LeagueAdvancedStats__cardTeamName'>{stats.player.currentTeam.name}</div>
          )}
          <div className='LeagueAdvancedStats__cardTitle'>{title}</div>
          <div className='LeagueAdvancedStats__cardValue'>
            <strong>{stats.value.toFixed(0)}</strong>&nbsp;<small>{value}</small>
          </div>
          <div className='LeagueAdvancedStats__cardMore'>
            <Button onClick={presentDetailStats} variant='dark-outline' small>
              {t('league.advancedStats.more')}
            </Button>
          </div>
        </div>
      </a>
    </Link>
  );
}

function PlayerRow({ title, stats, value, prefixes, leagueId, metric }: PlayerProps) {
  return (
    <Link href={`${prefixes.stats}/${leagueId}/${metric}`}>
      <a className='LeagueAdvancedStats__tableRow'>
        <div className='LeagueAdvancedStats__tableRowLabel'>{title}</div>
        <div className='LeagueAdvancedStats__tableRowName'>
          {stats.player.fullName}{' '}
          {stats.player.currentTeam && <> / {stats.player.currentTeam.name}</>} (
          {stats.value.toFixed(0)}&nbsp;{value})
        </div>
      </a>
    </Link>
  );
}

interface TeamProps {
  title: string;
  value: string;
  stats: LeagueAdvancedStatsTeamFragment;
  prefixes: { stats: string; team: string };
  leagueId: string;
  metric: LeagueAdvancedStatsPlayerMetric | LeagueAdvancedStatsTeamMetric;
}

function TeamCard({ title, stats, value, prefixes, leagueId, metric }: TeamProps) {
  const { t } = useTranslate();
  const { push } = useRouter();
  const presentDetailStats = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async e => {
      e.stopPropagation();
      e.preventDefault();
      await push(`${prefixes.stats}/${leagueId}/${metric}`);
    },
    [leagueId, metric, prefixes.stats, push],
  );

  return (
    <Link href={`${prefixes.team}/${stats.team.id}`}>
      <a className='LeagueAdvancedStats__card'>
        <div className='LeagueAdvancedStats__cardPhoto'>
          {stats.team.club.brandUrl && (
            <img src={stats.team.club.brandUrl} alt={stats.team.club.name} />
          )}
        </div>
        <div className='LeagueAdvancedStats__cardContent'>
          <div className='LeagueAdvancedStats__cardName'>{stats.team.name}</div>
          <div className='LeagueAdvancedStats__cardTitle'>{title}</div>
          <div className='LeagueAdvancedStats__cardValue'>
            <strong>{stats.value.toFixed(0)}</strong>&nbsp;<small>{value}</small>
          </div>
          <div className='LeagueAdvancedStats__cardMore'>
            <Button onClick={presentDetailStats} variant='dark-outline' small>
              {t('league.advancedStats.more')}
            </Button>
          </div>
        </div>
      </a>
    </Link>
  );
}

function TeamRow({
  title,
  stats,
  value,
  prefixes,
  digits,
  leagueId,
  metric,
}: TeamProps & { digits: number }) {
  return (
    <Link href={`${prefixes.stats}/${leagueId}/${metric}`}>
      <a className='LeagueAdvancedStats__tableRow'>
        <div className='LeagueAdvancedStats__tableRowLabel'>{title}</div>
        <div className='LeagueAdvancedStats__tableRowName'>
          {stats.team.name} ({stats.value.toFixed(digits)}&nbsp;{value})
        </div>
      </a>
    </Link>
  );
}

interface LeagueAdvancedStatsProps {
  leagueId: string;
  prefixes: { stats: string; team: string; player: string };
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function LeagueAdvancedStats({
  leagueId,
  prefixes,
  disableAssists,
  disablePoints,
}: LeagueAdvancedStatsProps) {
  const { t } = useTranslate();
  const { stats, loading } = useLeagueAdvancedStats(leagueId);

  if (!stats && loading) {
    return (
      <div className='LeagueAdvancedStats'>
        <div className='LeagueAdvancedStats__section'>
          <h2>{t('league.advancedStats.players.title')}</h2>
          <Loader size='small'>{t('league.advancedStats.players.loading')}</Loader>
        </div>
        <div className='LeagueAdvancedStats__section'>
          <h2>{t('league.advancedStats.teams.title')}</h2>
          <Loader size='small'>{t('league.advancedStats.teams.loading')}</Loader>
        </div>
      </div>
    );
  }

  if (!stats) return null;
  return (
    <div className='LeagueAdvancedStats'>
      <div className='LeagueAdvancedStats__section'>
        <h2>{t('league.advancedStats.players.title')}</h2>
        <div className='LeagueAdvancedStats__cards'>
          {stats.mostGoalsPlayer && (
            <PlayerCard
              title={t('league.advancedStats.mostGoalsPlayer')}
              value={t('league.advancedStats.mostGoalsPlayer.value')}
              stats={stats.mostGoalsPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostGoalsPlayer}
            />
          )}
          {stats.mostAssistsPlayer && !disableAssists && (
            <PlayerCard
              title={t('league.advancedStats.mostAssistsPlayer')}
              value={t('league.advancedStats.mostAssistsPlayer.value')}
              stats={stats.mostAssistsPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostAssistsPlayer}
            />
          )}
          {stats.mostMatchesPlayer && disableAssists && (
            <PlayerCard
              title={t('league.advancedStats.mostMatchesPlayer')}
              value={t('league.advancedStats.mostMatchesPlayer.value')}
              stats={stats.mostMatchesPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostMatchesPlayer}
            />
          )}
          {stats.mostGoalsPlayerInOneMatch && disablePoints && (
            <PlayerCard
              title={t('league.advancedStats.mostGoalsPlayerInOneMatch')}
              value={t('league.advancedStats.mostGoalsPlayerInOneMatch.value')}
              stats={stats.mostGoalsPlayerInOneMatch}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostGoalsPlayerInOneMatch}
            />
          )}
          {stats.mostProductivePlayer && !disablePoints && (
            <PlayerCard
              title={t('league.advancedStats.mostProductivePlayer')}
              value={t('league.advancedStats.mostProductivePlayer.value')}
              stats={stats.mostProductivePlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostProductivePlayer}
            />
          )}
          <div className='Header__crosses'>
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
          </div>
        </div>
        <div className='LeagueAdvancedStats__table'>
          {stats.mostYellowCardsPlayer && (
            <PlayerRow
              title={t('league.advancedStats.mostYellowCardsPlayer')}
              value={t('league.advancedStats.mostYellowCardsPlayer.value')}
              stats={stats.mostYellowCardsPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostYellowCardsPlayer}
            />
          )}
          {stats.mostRedCardsPlayer && (
            <PlayerRow
              title={t('league.advancedStats.mostRedCardsPlayer')}
              value={t('league.advancedStats.mostRedCardsPlayer.value')}
              stats={stats.mostRedCardsPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostRedCardsPlayer}
            />
          )}
          {stats.mostMatchesPlayer && !disableAssists && (
            <PlayerRow
              title={t('league.advancedStats.mostMatchesPlayer')}
              value={t('league.advancedStats.mostMatchesPlayer.value')}
              stats={stats.mostMatchesPlayer}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostMatchesPlayer}
            />
          )}
          {stats.mostGoalsPlayerInOneMatch && !disablePoints && (
            <PlayerRow
              title={t('league.advancedStats.mostGoalsPlayerInOneMatch')}
              value={t('league.advancedStats.mostGoalsPlayerInOneMatch.value')}
              stats={stats.mostGoalsPlayerInOneMatch}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostGoalsPlayerInOneMatch}
            />
          )}
          {stats.mostAssistsPlayerInOneMatch && !disableAssists && (
            <PlayerRow
              title={t('league.advancedStats.mostAssistsPlayerInOneMatch')}
              value={t('league.advancedStats.mostAssistsPlayerInOneMatch.value')}
              stats={stats.mostAssistsPlayerInOneMatch}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostAssistsPlayerInOneMatch}
            />
          )}
          {stats.mostPointsPlayerInOneMatch && !disablePoints && (
            <PlayerRow
              title={t('league.advancedStats.mostPointsPlayerInOneMatch')}
              value={t('league.advancedStats.mostPointsPlayerInOneMatch.value')}
              stats={stats.mostPointsPlayerInOneMatch}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsPlayerMetric.mostPointsPlayerInOneMatch}
            />
          )}
        </div>
      </div>
      <div className='LeagueAdvancedStats__section'>
        <h2>{t('league.advancedStats.teams.title')}</h2>
        <div className='LeagueAdvancedStats__cards'>
          {stats.mostGoalsTeam && (
            <TeamCard
              title={t('league.advancedStats.mostGoalsTeam')}
              value={t('league.advancedStats.mostGoalsTeam.value')}
              stats={stats.mostGoalsTeam}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostGoalsTeam}
            />
          )}
          {stats.leastReceivedGoalsTeam && (
            <TeamCard
              title={t('league.advancedStats.leastReceivedGoalsTeam')}
              value={t('league.advancedStats.leastReceivedGoalsTeam.value')}
              stats={stats.leastReceivedGoalsTeam}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.leastReceivedGoalsTeam}
            />
          )}
          {stats.mostReceivedGoalsTeam && (
            <TeamCard
              title={t('league.advancedStats.mostReceivedGoalsTeam')}
              value={t('league.advancedStats.mostReceivedGoalsTeam.value')}
              stats={stats.mostReceivedGoalsTeam}
              prefixes={prefixes}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostReceivedGoalsTeam}
            />
          )}
          <div className='Header__crosses'>
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
            <div className='Header__cross' />
          </div>
        </div>
        <div className='LeagueAdvancedStats__table'>
          {stats.mostYellowCardsTeam && (
            <TeamRow
              title={t('league.advancedStats.mostYellowCardsTeam')}
              value={t('league.advancedStats.mostYellowCardsTeam.value')}
              stats={stats.mostYellowCardsTeam}
              prefixes={prefixes}
              digits={0}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostYellowCardsTeam}
            />
          )}
          {stats.mostRedCardsTeam && (
            <TeamRow
              title={t('league.advancedStats.mostRedCardsTeam')}
              value={t('league.advancedStats.mostRedCardsTeam.value')}
              stats={stats.mostRedCardsTeam}
              prefixes={prefixes}
              digits={0}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostRedCardsTeam}
            />
          )}
          {stats.mostYoungTeam && (
            <TeamRow
              title={t('league.advancedStats.mostYoungTeam')}
              value={t('league.advancedStats.mostYoungTeam.value')}
              stats={stats.mostYoungTeam}
              prefixes={prefixes}
              digits={1}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostYoungTeam}
            />
          )}
          {stats.mostOldTeam && (
            <TeamRow
              title={t('league.advancedStats.mostOldTeam')}
              value={t('league.advancedStats.mostOldTeam.value')}
              stats={stats.mostOldTeam}
              prefixes={prefixes}
              digits={1}
              leagueId={leagueId}
              metric={LeagueAdvancedStatsTeamMetric.mostOldTeam}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueAdvancedStats;
